import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertCircle, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LinkedInAdsSelectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("No token provided");
      setLoading(false);
      return;
    }

    const fetchAccounts = async () => {
      try {
        const response = await axios.get(`${API}/api/integrations/linkedin-ads/accounts-popup`, {
          params: { token }
        });
        
        if (response.data.error) {
          setError(response.data.error);
        } else {
          setAccounts(response.data.accounts || []);
        }
      } catch (err) {
        console.error("Error fetching ad accounts:", err);
        setError(err.response?.data?.detail || "Failed to fetch ad accounts");
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [token]);

  const handleSelectAccount = async (account) => {
    setSelecting(true);
    setSelectedAccount(account.id);
    
    try {
      const response = await axios.post(`${API}/api/integrations/linkedin-ads/select-account-popup`, {
        token,
        account_id: account.id,
        account_name: account.name
      });
      
      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        navigate("/admin?tab=integrations&success=linkedin_ads");
      }
    } catch (err) {
      console.error("Error selecting account:", err);
      setError(err.response?.data?.detail || "Failed to select account");
      setSelecting(false);
      setSelectedAccount(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="text-slate-600">Loading ad accounts...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/admin?tab=integrations")} variant="outline">
              Back to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Select LinkedIn Ad Account
          </CardTitle>
          <CardDescription>
            Choose which ad account to connect for this tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No ad accounts found</p>
              <p className="text-sm text-slate-500 mb-4">
                Make sure you have access to LinkedIn Ad Accounts
              </p>
              <Button onClick={() => navigate("/admin?tab=integrations")} variant="outline">
                Back to Settings
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  onClick={() => handleSelectAccount(account)}
                  disabled={selecting}
                  className={`w-full p-4 rounded-lg border text-left transition-all ${
                    selectedAccount === account.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                  } ${selecting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{account.name}</p>
                      <p className="text-sm text-slate-500">ID: {account.id}</p>
                      {account.status && (
                        <p className="text-xs text-slate-400 mt-1">Status: {account.status}</p>
                      )}
                    </div>
                    {selectedAccount === account.id ? (
                      selecting ? (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LinkedInAdsSelectPage;
